export type PdfPreflightCode = 'VALID' | 'FILE_EMPTY' | 'PDF_INVALID'

export interface PdfPreflightResult {
  valid: boolean
  code: PdfPreflightCode
}

const PDF_HEADER = [0x25, 0x50, 0x44, 0x46, 0x2d] // %PDF-
const PDF_HEADER_SCAN_BYTES = 1024

const readBytes = (blob: Blob): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.onerror = () => reject(reader.error || new Error('读取文件失败'))
    reader.readAsArrayBuffer(blob)
  })

/** 浏览器端快筛；完整结构、MD5、加密和页数仍由服务端校验。 */
export const validatePdfPreflight = async (file: File): Promise<PdfPreflightResult> => {
  if (file.size === 0) {
    return { valid: false, code: 'FILE_EMPTY' }
  }
  if (file.size < PDF_HEADER.length) {
    return { valid: false, code: 'PDF_INVALID' }
  }

  const bytes = await readBytes(file.slice(0, Math.min(file.size, PDF_HEADER_SCAN_BYTES)))
  const hasPdfHeader = Array.from({ length: bytes.length - PDF_HEADER.length + 1 }).some(
    (_, start) => PDF_HEADER.every((byte, index) => bytes[start + index] === byte)
  )

  return hasPdfHeader
    ? { valid: true, code: 'VALID' }
    : { valid: false, code: 'PDF_INVALID' }
}

const VALIDATION_MESSAGES: Record<string, string> = {
  FILE_EMPTY: '文件内容为空，请重新选择',
  FILE_NOT_FOUND: '源文件已失效，请重新上传',
  FILE_SIZE_MISMATCH: '文件上传不完整，请重新上传',
  FILE_MD5_MISMATCH: '文件校验失败，请重新上传',
  PDF_INVALID: 'PDF 内容不完整或已损坏，请重新获取原文件',
  PDF_PASSWORD_REQUIRED: '暂不支持加密 PDF，请解除密码后重新上传',
  PDF_PAGE_LIMIT_EXCEEDED: 'PDF 页数超过当前支持范围',
  FILE_VALIDATION_BUSY: '文件正在校验，请稍后重试',
  FILE_VALIDATION_UNAVAILABLE: '文件校验服务暂不可用，请稍后重试'
}

export const getPdfValidationMessage = (error: any): string | undefined => {
  const validationCode = error?.data?.validationCode || error?.errorData?.data?.validationCode

  return validationCode ? VALIDATION_MESSAGES[validationCode] || error?.msg : undefined
}
